// React Component
import React from 'react'
import { NavLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Filter, ArrowDownWideNarrow, ArrowUpWideNarrow, LayoutGrid, List } from 'lucide-react'

// App Component
import { AppLayout, HeaderLayout } from '../../layouts'
import { FamlaServices } from '../../services'
import { listRecentCollectCollections, type RecentCollectCollectionSummary } from '../../services/api/collectDataService'
import { mergeProcessesAndDataCollections, type HomeRecentMergedEntry } from '../../utils/homeRecentMerge'
import { mergeProcessSearchWithDataCollections } from '../../utils/librarySearchMerge'
import { resolveCollectCardOwnerLabel } from '../../utils/collectCardOwnerLabel'
import {
    DEFAULT_MY_PROCESS_FILTERS,
    countActiveMyProcessFilters,
    filterAndSortMyProcesses,
    readStoredViewMode,
    storeViewMode,
    type MyProcessLibraryFilters,
    type MyProcessOwnerFilter,
    type MyProcessSort,
    type MyProcessStatusFilter,
    type MyProcessTypeFilter,
    type MyProcessViewMode,
} from '../../utils/myProcessesLibraryControls'
import { useAppSelector } from '../../stores'
import { Spiner } from '../../components'
import { Routes } from '../../routes'
import { useSearch } from '../../utils'
import {
    AddProcessOwnerModal,
    AssignProcessToFolderModal,
    EditprocessModal,
    GenericModal,
    PublishModal,
} from '../modal'

import { iProcess, iCompany } from '../../components/type'
import './process-library.css'
import './home-dashboard.css'
import DataCollectLibraryCard from './DataCollectLibraryCard'
import ProcessLibraryCard from './ProcessLibraryCard'
import HomeProcessRow from './HomeProcessRow'
import HomeDataCollectRow from './HomeDataCollectRow'

const ProcessViewAll = () => {
    const users = useAppSelector((state) => state.auth)
    const companyId = users.session?.company_id
    const { t } = useTranslation()
    const [notifProcess, setNotifProcess] = React.useState<{ [key: string]: boolean }>({})
    const [completedByProcessId, setCompletedByProcessId] = React.useState<
        Record<string, boolean | null | undefined>
    >({})
    const [process, setProcess] = React.useState<iProcess[]>([])
    const [dataCollections, setDataCollections] = React.useState<RecentCollectCollectionSummary[]>([])
    const [state, setState] = React.useState({
        isLoadingProcess: true,
        isLoadingApproval: true,
        requestApproval: [] as Array<any>,
        companyById: {} as iCompany,
    })
    const { searchQuery, setSearchQuery, searchResults, searchLoad, setFilter, filter } = useSearch('/process/search/my/process')
    const [viewMode, setViewMode] = React.useState<MyProcessViewMode>(() => readStoredViewMode())
    const [sort, setSort] = React.useState<MyProcessSort>('date_desc')
    const [filters, setFilters] = React.useState<MyProcessLibraryFilters>(DEFAULT_MY_PROCESS_FILTERS)
    const [filterOpen, setFilterOpen] = React.useState(false)
    const [listProcessDeleteId, setListProcessDeleteId] = React.useState<string | null>(null)
    const filterPanelRef = React.useRef<HTMLDivElement>(null)

    const currentUserId = users.user._id || users.session?.user?._id
    const currentUserFullname = users.user.fullname

    const mergedSearchResults = React.useMemo(() => {
        const safeSearchResults = searchResults ?? []
        return searchQuery
            ? mergeProcessSearchWithDataCollections(
                  safeSearchResults as iProcess[],
                  dataCollections,
                  searchQuery,
                  filter,
              )
            : []
    }, [searchResults, dataCollections, searchQuery, filter])

    const loadDataCollections = React.useCallback(async () => {
        if (!users.access_token) return
        const r = await listRecentCollectCollections(users.access_token, 100)
        if (r.status === 200 && Array.isArray(r.body?.data?.collections)) {
            setDataCollections(r.body.data.collections as RecentCollectCollectionSummary[])
        }
    }, [users.access_token])

    const mergedLibrary = React.useMemo(
        () => mergeProcessesAndDataCollections(process, dataCollections),
        [process, dataCollections],
    )

    const displayedEntries = React.useMemo(() => {
        const source = searchQuery ? mergedSearchResults : mergedLibrary
        return filterAndSortMyProcesses(source, {
            filters,
            sort,
            completedByProcessId,
            notifProcess,
            userId: currentUserId,
            userFullname: currentUserFullname,
        })
    }, [
        searchQuery,
        mergedSearchResults,
        mergedLibrary,
        filters,
        sort,
        completedByProcessId,
        notifProcess,
        currentUserId,
        currentUserFullname,
    ])

    const activeFilterCount = countActiveMyProcessFilters(filters)

    React.useEffect(() => {
        void loadDataCollections()
    }, [loadDataCollections])

    React.useEffect(() => {
        if (!filterOpen) return
        const onPointerDown = (event: MouseEvent) => {
            if (!filterPanelRef.current?.contains(event.target as Node)) {
                setFilterOpen(false)
            }
        }
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setFilterOpen(false)
        }
        document.addEventListener('mousedown', onPointerDown)
        document.addEventListener('keydown', onKeyDown)
        return () => {
            document.removeEventListener('mousedown', onPointerDown)
            document.removeEventListener('keydown', onKeyDown)
        }
    }, [filterOpen])

    React.useEffect(() => {
        if (!users.access_token || !companyId) return

        const loadCompanyById = async () => {
            try {
                const data = await FamlaServices(
                    'api_host',
                    `/company/by/id/${companyId}`,
                    'GET',
                    '',
                    users.access_token,
                )
                if (data.status === 200 && data.body?.data) {
                    setState((prev) => ({ ...prev, companyById: data.body.data }))
                }
            } catch {
                /* silent */
            }
        }

        const loadApprovalList = async () => {
            try {
                const data = await FamlaServices('api_host', `/approval/`, 'GET', '', users.access_token)
                if (data.status === 200) {
                    setState((prev) => ({
                        ...prev,
                        requestApproval: data.body.data,
                        isLoadingApproval: false,
                    }))
                } else {
                    setState((prev) => ({ ...prev, isLoadingApproval: false }))
                }
            } catch {
                setState((prev) => ({ ...prev, isLoadingApproval: false }))
            }
        }

        void loadCompanyById()
        void loadApprovalList()
    }, [users.access_token, companyId])

    const loadNotificationByProcess = async (processId: string) => {
        try {
            const data = await FamlaServices('api_host', `/notification/by/id/${processId}`, 'GET', '', users.access_token)
            if (data.status === 200) {
                return data.body.data
            }
            return false
        } catch (e) {
            return false
        }
    }

    const loadCompletionByProcess = async (processId: string): Promise<boolean> => {
        try {
            const data = await FamlaServices(
                'api_host',
                `/mapping/force-status/${processId}`,
                'GET',
                '',
                users.access_token,
            )
            if (
                data.status === 200 &&
                data.body?.data &&
                typeof data.body.data.all_interviews_completed === 'boolean'
            ) {
                return data.body.data.all_interviews_completed
            }
            return false
        } catch {
            return false
        }
    }

    React.useEffect(() => {
        const getProcessByUser = async () => {
            try {
                const data = await FamlaServices('api_host', `/process/my`, 'GET', '', users.access_token)

                if (data.status === 200) {
                    const processes = data.body.data as iProcess[]

                    const notif: { [key: string]: boolean } = {}
                    const completed: Record<string, boolean> = {}
                    await Promise.all(
                        processes.map(async (p) => {
                            const [hasNotif, isDone] = await Promise.all([
                                loadNotificationByProcess(p._id),
                                loadCompletionByProcess(p._id),
                            ])
                            notif[p._id] = Boolean(hasNotif)
                            completed[p._id] = isDone
                        }),
                    )
                    setNotifProcess(notif)
                    setCompletedByProcessId(completed)

                    setTimeout(() => {
                        setState((prev) => ({ ...prev, isLoadingProcess: false }))

                        setProcess((prevProcesses) => {
                            const processMap = new Map(prevProcesses.map((p) => [p._id, p]))
                            processes.forEach((newProcess: iProcess) => {
                                if (!processMap.has(newProcess._id)) {
                                    processMap.set(newProcess._id, newProcess)
                                }
                            })

                            return Array.from(processMap.values())
                        })
                        void loadDataCollections()
                    }, 500)
                }
            } catch (error) {
                // ignore
            }
        }

        getProcessByUser()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [users.access_token, loadDataCollections])

    const deleProcess = async (process_id: string) => {
        setProcess((prevState) => prevState.filter((p: { _id: string }) => p._id !== process_id))
        setListProcessDeleteId(null)
        await FamlaServices('api_host', `/process/${process_id}`, 'DELETE', '', users.access_token)
    }

    const updateProcess = (newData: any) => {
        setProcess((prev) =>
            prev.map((item) =>
                item._id === newData._id
                    ? {
                          ...item,
                          title: newData.title,
                          description: newData.description,
                          updated_at: newData.updated_at,
                      }
                    : item,
            ),
        )
    }

    const setViewModeAndPersist = (mode: MyProcessViewMode) => {
        setViewMode(mode)
        storeViewMode(mode)
    }

    const patchFilters = (patch: Partial<MyProcessLibraryFilters>) => {
        setFilters((prev) => ({ ...prev, ...patch }))
    }

    const clearFilters = () => {
        setFilters(DEFAULT_MY_PROCESS_FILTERS)
    }

    const renderProcessCard = (proc: iProcess, index: number) => {
        const hasNotif = notifProcess[proc._id] || false
        const ownerLabel = users.user.fullname === proc.owner.fullname ? t('words.me') : proc.owner.fullname
        return (
            <ProcessLibraryCard
                key={proc._id}
                proc={proc}
                index={index}
                hasNotif={hasNotif}
                ownerLabel={ownerLabel}
                deleProcess={deleProcess}
                updateProcess={updateProcess}
            />
        )
    }

    const renderMergedCard = (entry: HomeRecentMergedEntry, index: number) => {
        if (entry.kind === 'process') {
            return renderProcessCard(entry.item, index)
        }
        const ownerLabel = resolveCollectCardOwnerLabel(entry.item, t, {
            userId: currentUserId,
            userFullname: currentUserFullname,
        })
        return (
            <DataCollectLibraryCard
                key={`dc-${entry.item.id}`}
                col={entry.item}
                index={index}
                ownerLabel={ownerLabel}
                accessToken={users.access_token}
                onRefresh={() => void loadDataCollections()}
            />
        )
    }

    const renderMergedListRow = (entry: HomeRecentMergedEntry) => {
        if (entry.kind === 'process') {
            const isOwnerMe =
                Boolean(currentUserId && entry.item.owner?._id === currentUserId) ||
                users.user.fullname === entry.item.owner.fullname
            const ownerLabel = isOwnerMe ? t('words.me') : entry.item.owner.fullname
            return (
                <React.Fragment key={`list-p-${entry.item._id}`}>
                    <HomeProcessRow
                        process={entry.item}
                        ownerLabel={ownerLabel}
                        isOwnerMe={isOwnerMe}
                        hasNotif={notifProcess[entry.item._id] || false}
                        deleteProcess={() => void deleProcess(entry.item._id)}
                        setProcess={updateProcess}
                        onRequestDelete={() => setListProcessDeleteId(entry.item._id)}
                    />
                    <AddProcessOwnerModal
                        processId={entry.item._id}
                        modalId={`addProcessOwner${entry.item._id}`}
                    />
                    <AssignProcessToFolderModal processId={entry.item._id} processName={entry.item.title} />
                    <EditprocessModal
                        folder={false}
                        processId={entry.item._id}
                        processName={entry.item.title}
                        description={entry.item.description}
                        setProject={() => {}}
                        setProcess={updateProcess}
                    />
                    {listProcessDeleteId === entry.item._id ? (
                        <GenericModal
                            title={t('words.delete')}
                            actionButton={t('words.delete')}
                            action={() => void deleProcess(entry.item._id)}
                            sentence={t('views.processView.areYouSure')}
                            processId={entry.item._id}
                            autoShow
                            onHidden={() => setListProcessDeleteId(null)}
                        />
                    ) : null}
                    <PublishModal processId={entry.item._id} />
                </React.Fragment>
            )
        }

        return (
            <HomeDataCollectRow
                key={`list-c-${entry.item.id}`}
                collection={entry.item}
                ownerLabel={resolveCollectCardOwnerLabel(entry.item, t, {
                    userId: currentUserId,
                    userFullname: currentUserFullname,
                })}
                onCollectionUpdated={() => void loadDataCollections()}
            />
        )
    }

    const emptyMessage =
        activeFilterCount > 0
            ? t('views.processView.myProcessesNoFilterResults', {
                  defaultValue: 'No processes match these filters',
              })
            : t('views.processView.processNotFound')

    return (
        <AppLayout>
            <>
                <HeaderLayout
                    loadcompany={true}
                    listApproval={state.requestApproval}
                    loading={state.isLoadingApproval}
                    companyById={state.companyById}
                    search={true}
                    searchfilter={true}
                    setSearchQuery={setSearchQuery}
                    setFilter={setFilter}
                    filterValue={filter}
                />

                <div className="library-layout-root">
                    <div className="library-page-shell overflow-auto">
                        {state.isLoadingProcess ? (
                            <div className="library-dashboard d-flex justify-content-center pt-5">
                                <Spiner className="spinner-border-sm" />
                            </div>
                        ) : (
                            <div className="library-dashboard">
                                <div className="my-processes-header">
                                    <div className="my-processes-header-left">
                                        <NavLink
                                            to={Routes.PROCESSLIBRARY}
                                            className="my-processes-back"
                                            aria-label={t('words.back')}
                                        >
                                            <i className="bi bi-arrow-left" style={{ fontSize: '1.1rem' }} />
                                        </NavLink>
                                        <h1 className="my-processes-title">{t('views.processView.myProcess')}</h1>
                                    </div>

                                    <div className="my-processes-toolbar" role="toolbar" aria-label={t('views.processView.myProcessesToolbar')}>
                                        <button
                                            type="button"
                                            className="my-processes-tool-btn"
                                            onClick={() =>
                                                setSort((prev) => (prev === 'date_desc' ? 'date_asc' : 'date_desc'))
                                            }
                                            title={
                                                sort === 'date_desc'
                                                    ? t('views.processView.myProcessesSortNewest')
                                                    : t('views.processView.myProcessesSortOldest')
                                            }
                                            aria-label={
                                                sort === 'date_desc'
                                                    ? t('views.processView.myProcessesSortNewest')
                                                    : t('views.processView.myProcessesSortOldest')
                                            }
                                        >
                                            {sort === 'date_desc' ? (
                                                <ArrowDownWideNarrow size={16} strokeWidth={1.8} />
                                            ) : (
                                                <ArrowUpWideNarrow size={16} strokeWidth={1.8} />
                                            )}
                                            <span className="my-processes-tool-label">
                                                {sort === 'date_desc'
                                                    ? t('views.processView.myProcessesSortNewestShort')
                                                    : t('views.processView.myProcessesSortOldestShort')}
                                            </span>
                                        </button>

                                        <div className="my-processes-filter" ref={filterPanelRef}>
                                            <button
                                                type="button"
                                                className={`my-processes-tool-btn${activeFilterCount > 0 || filterOpen ? ' is-active' : ''}`}
                                                onClick={() => setFilterOpen((open) => !open)}
                                                aria-expanded={filterOpen}
                                                aria-haspopup="dialog"
                                            >
                                                <Filter size={16} strokeWidth={1.8} />
                                                <span className="my-processes-tool-label">{t('words.filter')}</span>
                                                {activeFilterCount > 0 ? (
                                                    <span className="my-processes-filter-badge">{activeFilterCount}</span>
                                                ) : null}
                                            </button>

                                            {filterOpen ? (
                                                <div className="my-processes-filter-panel" role="dialog" aria-label={t('words.filter')}>
                                                    <div className="my-processes-filter-group">
                                                        <div className="my-processes-filter-group-title">
                                                            {t('views.processView.myProcessesFilterStatus')}
                                                        </div>
                                                        {(
                                                            [
                                                                ['all', t('views.processView.myProcessesFilterAll')],
                                                                ['completed', t('views.processView.homeStatusCompleted')],
                                                                ['ongoing', t('views.processView.homeStatusOngoing')],
                                                            ] as Array<[MyProcessStatusFilter, string]>
                                                        ).map(([value, label]) => (
                                                            <label key={value} className="my-processes-filter-option">
                                                                <input
                                                                    type="radio"
                                                                    name="my-process-status"
                                                                    checked={filters.status === value}
                                                                    onChange={() => patchFilters({ status: value })}
                                                                />
                                                                <span>{label}</span>
                                                            </label>
                                                        ))}
                                                    </div>

                                                    <div className="my-processes-filter-group">
                                                        <div className="my-processes-filter-group-title">
                                                            {t('views.processView.myProcessesFilterOwner')}
                                                        </div>
                                                        {(
                                                            [
                                                                ['all', t('views.processView.myProcessesFilterAll')],
                                                                ['me', t('words.me')],
                                                                ['other', t('views.processView.myProcessesOwnerOther')],
                                                            ] as Array<[MyProcessOwnerFilter, string]>
                                                        ).map(([value, label]) => (
                                                            <label key={value} className="my-processes-filter-option">
                                                                <input
                                                                    type="radio"
                                                                    name="my-process-owner"
                                                                    checked={filters.owner === value}
                                                                    onChange={() => patchFilters({ owner: value })}
                                                                />
                                                                <span>{label}</span>
                                                            </label>
                                                        ))}
                                                    </div>

                                                    <div className="my-processes-filter-group">
                                                        <div className="my-processes-filter-group-title">
                                                            {t('views.processView.myProcessesFilterType')}
                                                        </div>
                                                        {(
                                                            [
                                                                ['all', t('views.processView.myProcessesFilterAll')],
                                                                ['AI', t('views.processView.homeTypeAI')],
                                                                ['DATA_COLLECT', t('views.processView.homeTypeDataCollect')],
                                                                ['HUMAN', t('views.processView.homeTypeHuman')],
                                                                ['IMAGE', t('views.processView.homeTypeImage')],
                                                                ['MANUAL', t('views.processView.homeTypeManual')],
                                                            ] as Array<[MyProcessTypeFilter, string]>
                                                        ).map(([value, label]) => (
                                                            <label key={value} className="my-processes-filter-option">
                                                                <input
                                                                    type="radio"
                                                                    name="my-process-type"
                                                                    checked={filters.type === value}
                                                                    onChange={() => patchFilters({ type: value })}
                                                                />
                                                                <span>{label}</span>
                                                            </label>
                                                        ))}
                                                    </div>

                                                    {activeFilterCount > 0 ? (
                                                        <button
                                                            type="button"
                                                            className="my-processes-filter-clear"
                                                            onClick={clearFilters}
                                                        >
                                                            {t('views.processView.myProcessesClearFilters')}
                                                        </button>
                                                    ) : null}
                                                </div>
                                            ) : null}
                                        </div>

                                        <div className="my-processes-view-toggle" role="group" aria-label={t('views.processView.myProcessesViewMode')}>
                                            <button
                                                type="button"
                                                className={`my-processes-view-btn${viewMode === 'grid' ? ' is-active' : ''}`}
                                                onClick={() => setViewModeAndPersist('grid')}
                                                aria-pressed={viewMode === 'grid'}
                                                title={t('views.processView.myProcessesViewGrid')}
                                            >
                                                <LayoutGrid size={16} strokeWidth={1.8} />
                                            </button>
                                            <button
                                                type="button"
                                                className={`my-processes-view-btn${viewMode === 'list' ? ' is-active' : ''}`}
                                                onClick={() => setViewModeAndPersist('list')}
                                                aria-pressed={viewMode === 'list'}
                                                title={t('views.processView.myProcessesViewList')}
                                            >
                                                <List size={16} strokeWidth={1.8} />
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {searchQuery !== '' ? (
                                    <div className="library-section-header library-search-block" style={{ marginBottom: 12 }}>
                                        <div className="library-section-title">{`${t('words.searching')}… ${searchQuery}`}</div>
                                    </div>
                                ) : null}

                                {viewMode === 'grid' ? (
                                    <div className="my-processes-grid">
                                        {displayedEntries.map((entry, index) => renderMergedCard(entry, index))}
                                        {!searchLoad && displayedEntries.length === 0 && (
                                            <div className="my-processes-empty">{emptyMessage}</div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="my-processes-list">
                                        {displayedEntries.map((entry) => renderMergedListRow(entry))}
                                        {!searchLoad && displayedEntries.length === 0 && (
                                            <div className="my-processes-empty">{emptyMessage}</div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </>
        </AppLayout>
    )
}

export default ProcessViewAll
